import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { FeelingId } from '../../constants/bridge';
import { HOLIDAY_LINES, holidayOf, seasonOf, type Holiday, type Season } from '../../constants/calendar';
import { play } from '../../lib/sound';
import { skyFor } from '../../constants/sky';
import Critter, { type Wear } from '../glyphs/Critter';

/**
 * Two shores and the bridge between them. The clinic sits on the right shore,
 * home on the left; every finished note lays one plank and the patient walks
 * a step closer to the next session. The sky is the patient's inner weather
 * crossed with the real time of day.
 */

interface Props {
  feeling: FeelingId | null;
  hour: number;
  total: number;
  done: number;
  avatarId?: string | null;
  /** Bumps each time a note is sent back, launching a paper plane. */
  flightKey?: number;
  heart?: boolean;
  /** First visit after a few days away: the critter says it waited. */
  welcomeBack?: boolean;
  /** Kind words the therapist sent this week: each one is a bird on the railing. */
  hearts?: { title: string }[];
  /** The therapist opened what came back from home: the clinic window lights up. */
  seen?: boolean;
  /** One river stone per finished task, oldest first, coloured by that day's sky. */
  stones?: (FeelingId | null)[];
  /** A milestone to celebrate (party hat and confetti), with the line the critter says. */
  celebrate?: string | null;
  therapistName?: string;
  /** For seasons and holidays; defaults to now. */
  date?: Date;
  className?: string;
}

const STONE: Record<string, string> = {
  stormy: '#6b6f86', rainy: '#7d9bb5', cloudy: '#b8b2a6', breezy: '#8fbf9f', sunny: '#f0b84a', none: '#c9b79c',
};

// Stones pile up on the home bank, nearest row first.
const STONE_SPOTS = (() => {
  const spots: { x: number; y: number; r: number }[] = [];
  for (const [row, y] of [318, 307, 296, 285, 274].entries()) {
    const maxX = 96 + (y - 262) * 0.2;
    for (let x = 9 + (row % 2) * 5; x < maxX; x += 11) spots.push({ x, y, r: ((x * 7 + y * 3) % 40) - 20 });
  }
  return spots;
})();

const CANOPY: Record<Season, string> = { autumn: '#d98a3a', winter: '#7f8f7a', spring: '#9fcf8f', summer: '#4f8f5f' };

const short = (t: string, n = 18) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

const TAP_LINES = ['היי!', 'אני פה איתך', 'נושמים ביחד?', 'צעד קטן זה גם צעד', 'איזה יום יפה לגשר', 'דגדוג!'];

function bubbleWidth(text: string) {
  return Math.max(44, text.length * 6.2 + 18);
}
// Keep the bubble inside the scene even when the critter stands at the very edge.
function bubbleLeft(text: string, at: number) {
  const w = bubbleWidth(text);
  return Math.min(Math.max(-w / 2, 6 - at), 384 - at - w);
}

const L = { x: 104, y: 262 }; // home anchor
const R = { x: 286, y: 262 }; // clinic anchor
const ARCH = 30;
const deckY = (t: number) => L.y - Math.sin(Math.PI * t) * ARCH;
const railY = (t: number) => deckY(t) - 24 + Math.sin(Math.PI * t) * 6;
const deckX = (t: number) => L.x + (R.x - L.x) * t;

const CLOUDS = [
  { x: 40, y: 58, s: 1 }, { x: 250, y: 40, s: 1.25 }, { x: 150, y: 82, s: 0.85 },
  { x: 330, y: 92, s: 0.9 }, { x: 95, y: 30, s: 1.1 }, { x: 205, y: 120, s: 0.7 },
];

function Cloud({ x, y, s, fill, i }: { x: number; y: number; s: number; fill: string; i: number }) {
  return (
    <motion.g
      initial={{ x: 0 }}
      animate={{ x: [0, i % 2 ? 18 : -18, 0] }}
      transition={{ duration: 24 + i * 5, repeat: Infinity, ease: 'easeInOut' }}
    >
      <g transform={`translate(${x} ${y}) scale(${s})`} fill={fill}>
        <ellipse cx="0" cy="8" rx="30" ry="12" />
        <circle cx="-12" cy="2" r="12" />
        <circle cx="8" cy="-3" r="16" />
        <circle cx="22" cy="5" r="10" />
      </g>
    </motion.g>
  );
}

function HolidayProp({ holiday }: { holiday: Holiday }) {
  const ink = { stroke: '#2f261e', strokeWidth: 1.4, strokeLinejoin: 'round' as const };
  switch (holiday.id) {
    case 'rosh-hashana':
      return (
        <g {...ink}>
          <circle cx="12" cy="14" r="8" fill="#d1453a" />
          <path d="M12 6 q1 -4 4 -5" fill="none" />
          <rect x="26" y="4" width="16" height="18" rx="4" fill="#f0a43a" />
          <rect x="25" y="1" width="18" height="5" rx="2" fill="#b5532f" />
        </g>
      );
    case 'sukkot':
      return (
        <g {...ink}>
          <path d="M0 6 H48 M4 6 V30 M44 6 V30" fill="none" stroke="#6b4a2f" strokeWidth="2.4" />
          <path d="M-2 6 q8 -8 14 0 q6 -8 12 0 q6 -8 12 0 q6 -8 14 0" fill="#4f8f5f" />
          {[12, 24, 36].map((x, i) => (
            <g key={x}>
              <path d={`M${x} 6 V${12 + i * 2}`} strokeWidth="0.8" fill="none" />
              <circle cx={x} cy={14 + i * 2} r="2.6" fill={['#e0603a', '#f0a43a', '#d1453a'][i]} />
            </g>
          ))}
        </g>
      );
    case 'hanukkah':
      return (
        <g>
          <rect x="2" y="22" width="48" height="5" rx="2" fill="#c9a24a" />
          <rect x="23" y="27" width="6" height="8" fill="#c9a24a" />
          {Array.from({ length: 9 }).map((_, i) => {
            const shamash = i === 4;
            const x = 4 + i * 5.5;
            // Candles stand from the right, one more each night; the shamash is always lit.
            const fromRight = i > 4 ? 8 - i : 7 - i;
            const lit = shamash || fromRight < holiday.night;
            return (
              <g key={i}>
                <rect x={x - 1.3} y={shamash ? 8 : 12} width="2.6" height={shamash ? 14 : 10} fill="#f4efe6" stroke="#2f261e" strokeWidth="0.5" />
                {lit && (
                  <ellipse cx={x} cy={shamash ? 5.5 : 9.5} rx="1.6" ry="2.6" fill="#ffb84a" className="window-glow" />
                )}
              </g>
            );
          })}
        </g>
      );
    case 'tu-bishvat':
      return (
        <g {...ink}>
          <path d="M20 30 V14" stroke="#6b4a2f" strokeWidth="2.4" fill="none" />
          <circle cx="20" cy="10" r="9" fill="#9fcf8f" />
          {[[15, 7], [24, 8], [20, 13], [14, 13]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="2" fill="#f6b8c8" strokeWidth="0.5" />
          ))}
        </g>
      );
    case 'purim':
      return (
        <g {...ink}>
          <path d="M4 10 Q20 2 36 10 Q34 22 20 20 Q6 22 4 10 Z" fill="#b5532f" />
          <ellipse cx="13" cy="12" rx="4" ry="3" fill="#fbf6ec" />
          <ellipse cx="27" cy="12" rx="4" ry="3" fill="#fbf6ec" />
          <path d="M36 10 l10 -6" fill="none" />
        </g>
      );
    case 'pesach':
      return (
        <g {...ink}>
          {[0, 1, 2].map((i) => (
            <g key={i} transform={`translate(${i * 3} ${-i * 5})`}>
              <rect x="4" y="18" width="30" height="10" rx="1.5" fill="#e9cf98" />
              <path d="M8 21 h22 M8 25 h22" stroke="#b08a4a" strokeWidth="0.8" strokeDasharray="1.5 2" />
            </g>
          ))}
        </g>
      );
    case 'shavuot':
      return (
        <g {...ink}>
          <path d="M6 14 H40 L36 30 H10 Z" fill="#c9a24a" />
          {[[12, 10, '#f6b8c8'], [20, 7, '#f0a43a'], [28, 9, '#e0603a'], [35, 11, '#fbf6ec']].map(([x, y, c], i) => (
            <circle key={i} cx={x as number} cy={y as number} r="4" fill={c as string} />
          ))}
        </g>
      );
  }
}

export default function ShoreScene({
  feeling, hour, total, done, avatarId, flightKey = 0, heart, welcomeBack,
  hearts = [], seen, stones = [], celebrate, therapistName = 'המטפל/ת', date, className = '',
}: Props) {
  const sky = skyFor(feeling, hour);
  const night = hour < 5 || hour >= 20;
  const n = Math.max(total, 1);
  const cloudCount = { sunny: 1, breezy: 2, cloudy: 4, rainy: 5, stormy: 6 }[feeling ?? 'breezy'] ?? 2;
  const cloudFill = feeling === 'stormy' ? '#5b6170' : feeling === 'rainy' ? '#9aa6b1' : night ? '#4b5878' : '#ffffff';
  const raining = feeling === 'rainy' || feeling === 'stormy';
  const showSun = !night && (feeling === 'sunny' || feeling === 'breezy' || feeling === null || feeling === 'cloudy');
  const allDone = total > 0 && done >= total;
  const today = date ?? new Date();
  const season = seasonOf(today);
  const holiday: Holiday | null = holidayOf(today);
  const sleeping = night && (allDone || total === 0);
  const morning = hour >= 6 && hour < 10;
  const lunch = hour >= 12 && hour < 14;
  const party = !!celebrate || holiday?.id === 'purim';

  // ── the living critter ──
  const [jumpKey, setJumpKey] = useState(0);
  const [bubble, setBubble] = useState<string | null>(null);
  const bubbleTimer = useRef<number>();
  const say = (text: string | null, ms = 3800) => {
    window.clearTimeout(bubbleTimer.current);
    setBubble(text);
    if (text) bubbleTimer.current = window.setTimeout(() => setBubble(null), ms);
  };
  useEffect(() => {
    if (sleeping) return;
    if (welcomeBack) say('חיכיתי לך!', 5000);
    else if (holiday) say(HOLIDAY_LINES[holiday.id], 4500);
    else if (allDone) say('הגשר שלם להיום!');
    else if (done === 0 && hour >= 17) say('נעשה אחד ביחד?', 5000);
    else if (morning) say('בוקר! רגע, מתמתח...', 3500);
    else if (lunch) say('הפסקת צהריים קטנה', 3500);
    return () => window.clearTimeout(bubbleTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (flightKey > 0) {
      setJumpKey((k) => k + 1);
      say(allDone ? 'הגשר שלם להיום!' : 'יש! עוד קרש');
      play(allDone ? 'cascade' : 'drip');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightKey]);
  useEffect(() => {
    if (!celebrate) return;
    setJumpKey((k) => k + 1);
    say(celebrate, 5000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebrate]);
  const poke = () => {
    setJumpKey((k) => k + 1);
    say(TAP_LINES[Math.floor(Math.random() * TAP_LINES.length)]);
  };
  const wear: Wear | null = party
    ? 'partyhat'
    : raining
      ? 'umbrella'
      : night
        ? 'nightcap'
        : lunch
          ? 'snack'
          : feeling === 'sunny'
            ? 'sunglasses'
            : feeling === 'breezy' || feeling === 'cloudy'
              ? 'scarf'
              : null;
  const waiting = done === 0 && hour >= 17 && !allDone && !sleeping;

  // Where the patient stands: at home, on the last laid plank, or at the clinic door.
  const walker = allDone
    ? { x: 330, y: 234 }
    : done === 0
      ? { x: 74, y: 234 }
      : { x: deckX((done - 0.5) / n), y: deckY((done - 0.5) / n) - 14 };

  return (
    <svg
      viewBox="0 0 390 360"
      preserveAspectRatio="xMidYMax slice"
      className={className}
      role="img"
      aria-label={`נוף הגשר: ${done} מתוך ${total} קרשים הונחו היום`}
    >
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={sky.top} />
          <stop offset="1" stopColor={sky.bottom} />
        </linearGradient>
        <linearGradient id="water" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={sky.water} />
          <stop offset="1" stopColor={sky.near} stopOpacity="0.9" />
        </linearGradient>
        <radialGradient id="glow">
          <stop offset="0" stopColor="#ffd88a" stopOpacity="0.9" />
          <stop offset="1" stopColor="#ffd88a" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="390" height="360" fill="url(#sky)" />

      {night && (
        <g fill="#fff">
          {[[30, 30], [80, 70], [140, 24], [190, 60], [240, 18], [300, 50], [350, 28], [60, 120], [210, 100], [365, 110]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={i % 3 ? 1 : 1.6} className="star-twinkle" style={{ animationDelay: `${i * 0.4}s` }} />
          ))}
          <circle cx="70" cy="132" r="18" fill="#f6efd9" />
          <circle cx="78" cy="126" r="16" fill={sky.top} />
        </g>
      )}

      {showSun && (
        <g>
          <circle cx="70" cy="134" r="46" fill="url(#glow)" />
          <motion.circle
            cx="70" cy="134" r="22" fill={hour >= 17 ? '#f7a35c' : '#ffd36e'}
            style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            animate={{ scale: [1, 1.07, 1] }}
            transition={{ duration: 6, repeat: Infinity }}
          />
        </g>
      )}

      {CLOUDS.slice(0, cloudCount).map((c, i) => (
        <Cloud key={i} {...c} fill={cloudFill} i={i} />
      ))}

      {(feeling === 'sunny' || feeling === 'breezy') && !night && (
        <motion.g
          animate={{ x: [0, 40], y: [0, -6] }}
          transition={{ duration: 18, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
          stroke="#44505c" strokeWidth="1.4" fill="none" strokeLinecap="round"
        >
          <path d="M180 70 q4 -4 8 0 q4 -4 8 0" />
          <path d="M200 84 q3 -3 6 0 q3 -3 6 0" />
        </motion.g>
      )}

      {/* far hills */}
      <path d="M0 238 C60 204 130 222 196 232 C262 216 330 200 390 222 L390 360 L0 360 Z" fill={sky.far} />

      {/* river */}
      <rect x="0" y="268" width="390" height="92" fill="url(#water)" />
      <g stroke="#fff" strokeOpacity="0.35" strokeWidth="1.5" fill="none" className="wave-drift">
        <path d="M-40 292 q15 -5 30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0" />
        <path d="M-25 316 q15 -5 30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0 t30 0" />
      </g>

      {/* shores */}
      <path d="M0 226 C44 216 88 228 110 262 L124 360 L0 360 Z" fill={sky.near} />
      <path d="M390 220 C346 210 302 226 280 262 L266 360 L390 360 Z" fill={sky.near} />

      {/* the season's tree by the house */}
      <g aria-hidden="true">
        <path d="M86 238 V214 M86 222 l-6 -6 M86 218 l6 -7" stroke="#6b4a2f" strokeWidth="3" strokeLinecap="round" fill="none" />
        {season !== 'winter' || holiday?.id === 'tu-bishvat' ? (
          <g>
            <circle cx="86" cy="206" r="13" fill={holiday?.id === 'tu-bishvat' ? CANOPY.spring : CANOPY[season]} />
            <circle cx="77" cy="212" r="8" fill={holiday?.id === 'tu-bishvat' ? CANOPY.spring : CANOPY[season]} opacity="0.9" />
            {(season === 'spring' || holiday?.id === 'tu-bishvat' || holiday?.id === 'pesach') &&
              [[80, 200], [90, 198], [94, 208], [83, 210], [74, 214], [88, 213]].map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="2.2" fill="#f6b8c8" stroke="#fff" strokeWidth="0.6" />
              ))}
          </g>
        ) : (
          <path d="M86 214 l-9 -10 M86 212 l8 -12 M80 207 l-4 -6" stroke="#6b4a2f" strokeWidth="2" strokeLinecap="round" />
        )}
      </g>
      {season === 'autumn' &&
        [0, 1, 2, 3, 4].map((i) => (
          <motion.path
            key={`leaf${i}`}
            d="M0 0 q4 -5 8 0 q-4 5 -8 0 Z"
            fill={i % 2 ? '#d98a3a' : '#c4612f'}
            initial={{ x: 60 + i * 9, y: 200, opacity: 0, rotate: 0 }}
            animate={{ x: [60 + i * 9, 40 + i * 30, 70 + i * 22], y: [200, 240, 268], opacity: [0, 1, 0], rotate: [0, 120, 260] }}
            transition={{ duration: 7 + i, repeat: Infinity, delay: i * 2.3, ease: 'easeInOut' }}
          />
        ))}

      {/* river stones: one for every finished task, in that day's colour */}
      <g aria-label={stones.length ? `${stones.length} אבנים על הגדה, אחת לכל משימה שהושלמה` : undefined}>
        {stones.slice(-STONE_SPOTS.length).map((f, i, arr) => {
          const spot = STONE_SPOTS[i];
          const fresh = i === arr.length - 1;
          return (
            <motion.ellipse
              key={i}
              cx={spot.x} cy={spot.y} rx="4.8" ry="3.3"
              transform={`rotate(${spot.r} ${spot.x} ${spot.y})`}
              fill={STONE[f ?? 'none']} stroke="#00000033" strokeWidth="0.8"
              initial={fresh ? { opacity: 0, y: -14 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, type: 'spring', stiffness: 200, damping: 12 }}
            />
          );
        })}
      </g>

      {/* home (left) */}
      <g transform="translate(18 170) scale(1.35)">
        <rect x="0" y="16" width="34" height="26" rx="3" fill="#f3e6d0" />
        <path d="M-4 18 L17 0 L38 18 Z" fill="#b5532f" />
        <rect x="13" y="28" width="9" height="14" rx="2" fill="#7a4a2a" />
        <rect x="4" y="22" width="7" height="7" rx="1.5" fill={night ? '#ffd27a' : '#cfe3ea'} />
      </g>
      <text x="41" y="246" textAnchor="middle" fontSize="10" fontWeight="700" fill={sky.dark ? '#f4efe6' : '#2f261e'}>הבית</text>

      {/* clinic (right) */}
      {seen && <circle cx="345" cy="198" r="26" fill="url(#glow)" />}
      <g
        transform="translate(316 162) scale(1.3)"
        role="button"
        tabIndex={0}
        aria-label={seen ? `${therapistName} ראה/תה את מה ששלחת` : 'הקליניקה'}
        style={{ cursor: 'pointer', outline: 'none' }}
        onClick={() => say(seen ? `${short(therapistName, 10)} ראה/תה מה ששלחת` : 'יגיע לפני הפגישה')}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && say(seen ? `${short(therapistName, 10)} ראה/תה מה ששלחת` : 'יגיע לפני הפגישה')}
      >
        <rect x="0" y="16" width="44" height="32" rx="3" fill="#fbf4e6" />
        <path d="M-5 18 L22 -2 L49 18 Z" fill="#1d5249" />
        <rect x="17" y="32" width="10" height="16" rx="2" fill="#1d5249" />
        <rect x="5" y="23" width="8" height="8" rx="1.5" fill={seen ? '#ffd27a' : '#cfe3ea'} className={seen ? 'window-glow' : ''} />
        <rect x="31" y="23" width="8" height="8" rx="1.5" fill={seen ? '#ffd27a' : '#cfe3ea'} className={seen ? 'window-glow' : ''} />
      </g>
      <text x="344" y="246" textAnchor="middle" fontSize="10" fontWeight="700" fill={sky.dark ? '#f4efe6' : '#2f261e'}>הקליניקה</text>

      {holiday && (
        <g
          transform="translate(280 280) scale(1.2)"
          role="button" tabIndex={0} aria-label={HOLIDAY_LINES[holiday.id]}
          style={{ cursor: 'pointer', outline: 'none' }}
          onClick={() => say(HOLIDAY_LINES[holiday.id])}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && say(HOLIDAY_LINES[holiday.id])}
        >
          <HolidayProp holiday={holiday} />
        </g>
      )}

      {heart && (
        <motion.g
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <circle cx="345" cy="140" r="16" fill="url(#glow)" />
          <path d="M345 148 l-7 -7 a4.5 4.5 0 0 1 7 -6 a4.5 4.5 0 0 1 7 6 Z" fill="#e0603a" />
        </motion.g>
      )}

      {/* bridge */}
      <g>
        {/* deck beam, so the bridge reads as a bridge even before any plank */}
        <path
          d={`M${L.x} ${L.y + 6} Q ${(L.x + R.x) / 2} ${L.y + 6 - ARCH * 2} ${R.x} ${R.y + 6}`}
          fill="none" stroke="#6b4a2f" strokeWidth="3" strokeLinecap="round" opacity="0.85"
        />
        {Array.from({ length: 9 }).map((_, i) => {
          const t = (i + 1) / 10;
          return (
            <line key={i} x1={deckX(t)} y1={deckY(t)} x2={deckX(t)} y2={railY(t)}
              stroke="#6b4a2f" strokeWidth="1" opacity="0.55" />
          );
        })}
        <path
          d={Array.from({ length: 21 }).map((_, i) => `${i ? 'L' : 'M'}${deckX(i / 20).toFixed(1)} ${railY(i / 20).toFixed(1)}`).join(' ')}
          fill="none" stroke="#6b4a2f" strokeWidth="2" strokeLinecap="round"
        />
        {[L, R].map((p, i) => (
          <g key={i}>
            <rect x={p.x - 3} y={p.y - 30} width="6" height="34" rx="2" fill="#6b4a2f" />
            <circle cx={p.x} cy={p.y - 31} r="4" fill="#f0a43a" />
          </g>
        ))}
        {Array.from({ length: total }).map((_, i) => {
          const t = (i + 0.5) / n;
          const x = deckX(t);
          const y = deckY(t);
          const slope = (Math.cos(Math.PI * t) * -ARCH * Math.PI) / (R.x - L.x);
          const angle = (Math.atan(slope) * 180) / Math.PI;
          const w = (R.x - L.x) / n - 4;
          const laid = i < done;
          return (
            <g key={i} transform={`translate(${x} ${y}) rotate(${angle})`}>
              {laid ? (
                <motion.rect
                  x={-w / 2} y={-6} width={w} height={12} rx={3}
                  fill="#f0a43a" stroke="#a0631a" strokeWidth="1"
                  initial={{ y: -46, opacity: 0, rotate: -12 }}
                  animate={{ y: -6, opacity: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.2 + i * 0.12 }}
                />
              ) : (
                <rect x={-w / 2} y={-6} width={w} height={12} rx={3} fill={sky.dark ? '#ffffff22' : '#ffffff66'} stroke={sky.dark ? '#f4efe6' : '#4a3b2c'} strokeOpacity="0.45" strokeDasharray="3 3" />
              )}
            </g>
          );
        })}
      </g>

      {/* a bird on the railing for every kind word this week */}
      {hearts.slice(0, 5).map((h, i) => {
        const t = [0.22, 0.78, 0.4, 0.62, 0.5][i];
        const x = deckX(t);
        const y = railY(t) - 4;
        const line = `מילה טובה על "${short(h.title)}"`;
        return (
          <motion.g
            key={i}
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: [0, -1.5, 0] }}
            transition={{ opacity: { delay: 0.4 + i * 0.2 }, y: { duration: 2.6 + i * 0.3, repeat: Infinity, ease: 'easeInOut' } }}
            role="button" tabIndex={0} aria-label={line}
            style={{ cursor: 'pointer', outline: 'none' }}
            onClick={() => { play('chirp'); say(line, 4500); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { play('chirp'); say(line, 4500); } }}
          >
            <g transform={`translate(${x} ${y}) scale(${i % 2 ? -1 : 1} 1)`}>
              <ellipse cx="0" cy="0" rx="6" ry="4.5" fill={i % 2 ? '#e0603a' : '#2e6b60'} />
              <circle cx="4.5" cy="-3.5" r="3.2" fill={i % 2 ? '#e0603a' : '#2e6b60'} />
              <circle cx="5.4" cy="-4.2" r="0.8" fill="#fff" />
              <path d="M7.4 -3.4 l3 0.8 l-3 0.8 Z" fill="#f0a43a" />
              <path d="M-5 -1 l-5 -2.5 l1 4 Z" fill={i % 2 ? '#c24e2c' : '#1d5249'} />
            </g>
          </motion.g>
        );
      })}

      {/* the patient: a living critter */}
      <motion.g
        initial={false}
        animate={{ x: walker.x, y: walker.y }}
        transition={{ type: 'spring', stiffness: 90, damping: 14 }}
      >
        <ellipse cx="0" cy="14" rx="11" ry="2.6" fill="#000" opacity="0.18" />
        <motion.g
          key={jumpKey}
          initial={{ y: 0 }}
          animate={
            jumpKey
              ? { y: [0, -20, 0, -6, 0], scaleY: [1, 1.06, 0.92, 1.02, 1] }
              : morning && !sleeping
                ? { y: [0, -4, 0], scaleY: [1, 1.16, 1], scaleX: [1, 0.9, 1] }
                : { y: 0 }
          }
          transition={jumpKey ? { duration: 0.7, ease: 'easeOut' } : { duration: 1.4, delay: 0.8, ease: 'easeInOut' }}
        >
          <g
            className="critter-breathe"
            role="button"
            tabIndex={0}
            aria-label="היצור שלי"
            style={{ cursor: 'pointer', outline: 'none' }}
            onClick={poke}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && poke()}
            transform={waiting ? 'translate(0 3) scale(1 0.9)' : undefined}
          >
            <Critter
              id={avatarId}
              size={34}
              x={-17}
              y={-20}
              blink
              wear={wear}
              expression={sleeping ? 'sleep' : jumpKey > 0 && bubble ? 'joy' : null}
            />
          </g>
          {jumpKey > 0 && (
            <g key={`h${jumpKey}`}>
              {[-14, 0, 14].map((dx, i) => (
                <motion.path
                  key={i}
                  d="M0 3 l-3.2 -3.2 a2 2 0 0 1 3.2 -2.6 a2 2 0 0 1 3.2 2.6 Z"
                  fill={i === 1 ? '#f0a43a' : '#e0603a'}
                  initial={{ x: 0, y: -18, opacity: 0, scale: 0.4 }}
                  animate={{ x: dx, y: -38 - i * 4, opacity: [0, 1, 0], scale: 1 }}
                  transition={{ duration: 1.1, delay: i * 0.08 }}
                />
              ))}
            </g>
          )}
        </motion.g>

        {sleeping && (
          <g fontSize="9" fontWeight="800" fill={sky.dark ? '#f4efe6' : '#2f261e'} aria-label="היצור ישן">
            {[0, 1, 2].map((i) => (
              <motion.text
                key={i} x={10} y={-16} textAnchor="middle"
                animate={{ x: [10, 18 + i * 4], y: [-16, -40 - i * 4], opacity: [0, 1, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: i, ease: 'easeOut' }}
              >
                z
              </motion.text>
            ))}
          </g>
        )}

        <AnimatePresence>
          {bubble && (
            <motion.g
              key={bubble}
              initial={{ opacity: 0, y: 6, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              aria-live="polite"
            >
              <rect
                x={bubbleLeft(bubble, walker.x)} y={-52} width={bubbleWidth(bubble)} height={20} rx={10}
                fill="#fefcf9" stroke="#2f261e" strokeWidth="1.4"
              />
              <path d="M-4 -32.6 L0 -27 L4 -32.6" fill="#fefcf9" stroke="#2f261e" strokeWidth="1.4" strokeLinejoin="round" />
              <rect x={-5} y={-34} width={10} height={2.4} fill="#fefcf9" />
              <text x={bubbleLeft(bubble, walker.x) + bubbleWidth(bubble) / 2} y="-38.5" textAnchor="middle" direction="rtl" fontSize="10.5" fontWeight="700" fill="#2f261e">
                {bubble}
              </text>
            </motion.g>
          )}
        </AnimatePresence>
      </motion.g>

      {/* a note flying back to the clinic */}
      <AnimatePresence>
        {flightKey > 0 && (
          <motion.g
            key={flightKey}
            initial={{ x: walker.x, y: walker.y - 10, opacity: 0, rotate: -10 }}
            animate={{
              x: [walker.x, (walker.x + 340) / 2, 344],
              y: [walker.y - 10, 80, 176],
              opacity: [0, 1, 1, 0],
              rotate: [-10, 8, 20],
            }}
            transition={{ duration: 2.2, ease: 'easeInOut' }}
          >
            <path d="M-10 0 L12 -6 L2 8 L0 2 Z" fill="#fff" stroke="#1d5249" strokeWidth="1" />
            <path d="M0 2 L12 -6" stroke="#1d5249" strokeWidth="0.8" />
          </motion.g>
        )}
      </AnimatePresence>

      {/* weather in front */}
      {raining && (
        <g stroke={night ? '#9fb3d1' : '#e6eef5'} strokeWidth="1.2" strokeLinecap="round" opacity="0.8">
          {Array.from({ length: feeling === 'stormy' ? 46 : 30 }).map((_, i) => {
            const x = (i * 53) % 400;
            return (
              <line
                key={i} x1={x} y1={-20} x2={x - 4} y2={-8}
                className="rain-drop"
                style={{ animationDelay: `${(i * 0.137) % 1.1}s`, animationDuration: `${0.8 + (i % 4) * 0.12}s` }}
              />
            );
          })}
        </g>
      )}
      {feeling === 'stormy' && <rect width="390" height="360" fill="#fff" className="storm-flash" />}
      {party && (
        <g key={celebrate ?? 'purim'} aria-hidden="true">
          {Array.from({ length: 24 }).map((_, i) => (
            <motion.rect
              key={i} width="5" height="8" rx="1"
              fill={['#f0a43a', '#e0603a', '#2e6b60', '#8fbf9f', '#f6b8c8'][i % 5]}
              initial={{ x: (i * 37) % 390, y: -20, rotate: 0, opacity: 1 }}
              animate={{ y: 230 + (i % 5) * 12, rotate: 360 + i * 30, opacity: [1, 1, 0] }}
              transition={{ duration: 2.6 + (i % 4) * 0.4, delay: 0.3 + (i % 6) * 0.15, ease: 'easeIn' }}
            />
          ))}
        </g>
      )}
    </svg>
  );
}
